import { Badge } from '@/components/ui/badge'
import { formatTimelineTimestamp } from './timelineMath'

type PhaseDivider = {
  name: string
  at: number
}

type EncounterEntry = {
  time: number
  phaseIndex: number
}

type AssignmentPhaseTimestampBadgeData = {
  phaseLabel?: string
  timeLabel: string
}

const buildPhaseLabel = (
  phaseIndex: number,
  phaseDividers: PhaseDivider[],
): string => {
  const phaseName = phaseDividers[phaseIndex]?.name?.trim() ?? ''
  const firstLetter = phaseName[0]?.toUpperCase() ?? 'P'
  const numberFromName = phaseName.match(/\d+/)?.[0]
  const phaseNumber = numberFromName ?? String(phaseIndex + 1)
  return `${firstLetter}${phaseNumber}`
}

const buildAssignmentPhaseTimestampBadgeData = ({
  castIndexes,
  entries,
  phaseDividers,
}: {
  castIndexes: number[]
  entries: EncounterEntry[]
  phaseDividers: PhaseDivider[]
}): AssignmentPhaseTimestampBadgeData => {
  const firstEntry = entries[castIndexes[0]]
  const phaseIndex = firstEntry?.phaseIndex ?? 0
  const phaseLabel =
    phaseDividers.length > 1
      ? buildPhaseLabel(phaseIndex, phaseDividers)
      : undefined
  const timeLabel = castIndexes
    .map((castIndex) => {
      const entry = entries[castIndex]
      if (!entry) return formatTimelineTimestamp(0)
      return formatTimelineTimestamp(entry.time)
    })
    .join(' / ')
  return { phaseLabel, timeLabel }
}

const AssignmentPhaseTimestampBadge = ({
  phaseLabel,
  timeLabel,
}: AssignmentPhaseTimestampBadgeData) => {
  if (!phaseLabel) {
    return <Badge variant='outline'>{timeLabel}</Badge>
  }
  return (
    <Badge variant='outline' className='h-6 gap-0 p-0 overflow-hidden'>
      <span className='pl-1.5 pr-1 h-full inline-flex items-center'>
        {phaseLabel}
      </span>
      <span className='w-px h-full bg-border' />
      <span className='pl-1 pr-1.5 h-full inline-flex items-center bg-accent'>
        {timeLabel}
      </span>
    </Badge>
  )
}

export type { PhaseDivider, AssignmentPhaseTimestampBadgeData }
export { AssignmentPhaseTimestampBadge, buildAssignmentPhaseTimestampBadgeData }
